import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import {
	getProductAndIDFromARI,
	type SearchResultType as SearchClientResultType,
} from '@atlassian/search-client';
import { highlightMatchedText } from '@atlassian/search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { TalentUserAvatar } from '../../../../../../common/ui/talent-user-avatar';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchTalentPosition_AtlassianSearchPage$key } from './__generated__/searchTalentPosition_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { PositionFocusArea } from './position-focus-area';
import type { positionFocusArea_AtlassianSearchPage$key } from './position-focus-area/__generated__/positionFocusArea_AtlassianSearchPage.graphql';

export const SearchTalentPosition = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	searchQuery,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchTalentPosition_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchTalentPosition_AtlassianSearchPage on SearchResult {
				__typename

				id
				type
				url
				... on SearchResultTalentPosition {
					__typename
					position {
						__typename
						worker {
							preferredName
							user {
								picture
							}
						}
						fieldValues(
							fieldIdIsIn: [
								"positionId"
								"positionStatus"
								"positionJobTitle"
								"positionPositionTitle"
								"positionKey"
								"positionExternalPositionKey"
								"positionWorker"
								"focusArea"
							]
						) {
							fieldId
							fieldValue {
								__typename
								... on RadarStringFieldValue {
									__typename
									stringValue: value
								}
								... on RadarAriFieldValue {
									__typename
									value {
										__typename
										... on MercuryFocusArea {
											...positionFocusArea_AtlassianSearchPage
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const preferredName: string | undefined = data.position?.worker?.preferredName || undefined;

	const [jobTitle, positionExternalKey, focusArea, positionKey] = useMemo(() => {
		let jobTitle = '';
		let positionExternalKey = '';
		let focusArea: positionFocusArea_AtlassianSearchPage$key | null = null;
		let positionKey = '';

		data.position?.fieldValues?.forEach((fieldValue) => {
			if (
				fieldValue.fieldId === 'positionKey' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				positionKey = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'positionPositionTitle' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				jobTitle = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'positionExternalPositionKey' &&
				fieldValue.fieldValue?.__typename === 'RadarStringFieldValue'
			) {
				positionExternalKey = fieldValue.fieldValue?.stringValue || '';
			}
			if (
				fieldValue.fieldId === 'focusArea' &&
				fieldValue.fieldValue?.__typename === 'RadarAriFieldValue' &&
				fieldValue.fieldValue?.value?.__typename === 'MercuryFocusArea'
			) {
				focusArea = fieldValue.fieldValue?.value;
			}
		});

		return [jobTitle, positionExternalKey, focusArea, positionKey];
	}, [data.position?.fieldValues]);

	const title = useMemo(() => {
		const title = formatMessage(messages.positionTitle, {
			positionKey: positionKey,
			preferredName: preferredName,
		});
		if (!searchQuery) {
			return enrichExcerpt(title) || title;
		}

		const { highlightedTitle } = highlightMatchedText(title, searchQuery.split(' '));
		return !highlightedTitle ? enrichExcerpt(title) : enrichExcerpt(highlightedTitle);
	}, [positionKey, preferredName, formatMessage, searchQuery]);

	const headerMetadataParts = useMemo(
		() => [
			['type', <SearchResultType type={data.type as SearchClientResultType} />],
			jobTitle ? ['positionTitle', jobTitle] : null,
			positionExternalKey
				? [
						'positionExternalKey',
						formatMessage(messages.positionExternalKey, { positionExternalKey }),
					]
				: null,
		],
		[data.type, jobTitle, positionExternalKey, formatMessage],
	);

	return (
		<SearchResult
			{...data}
			hideSummarizeInChat={true}
			product={ProductKeys.Talent}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={title}
			icon={<TalentUserAvatar src={data.position?.worker?.user?.picture} />}
			headerMetadataParts={headerMetadataParts}
			description={<PositionFocusArea node={focusArea} />}
			cloudId={cloudId}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchTalentPosition = (
	node?: any,
): node is searchTalentPosition_AtlassianSearchPage$key => {
	return node?.id?.includes('radar') && node.__typename === 'SearchResultTalentPosition';
};
